import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiBaseService } from './api/api-base.service';
import { AuthService } from './api/auth.service';

export interface TwoFactorModalContext {
  mfaToken: string;
  error: string | null;
  submitting: boolean;
  onSuccess: (response: any) => void;
  onCancel?: () => void;
}

export interface TwoFactorModalState {
  isOpen: boolean;
  context: TwoFactorModalContext | null;
}

@Injectable({
  providedIn: 'root'
})
export class TwoFactorAuthService extends ApiBaseService {
  private readonly authService = inject(AuthService);

  private readonly stateSubject = new BehaviorSubject<TwoFactorModalState>({
    isOpen: false,
    context: null
  });

  readonly state$ = this.stateSubject.asObservable();

  enroll(): Observable<any> {
    return this.POST(this.getUrl('/auth/mfa/enroll'), {});
  }

  confirm(code: string): Observable<any> {
    return this.POST(this.getUrl('/auth/mfa/confirm'), { code });
  }

  disable(code: string): Observable<any> {
    return this.POST(this.getUrl('/auth/mfa/disable'), { code });
  }

  disableForUser(userId: number): Observable<any> {
    return this.POST(this.getUrl(`/auth/mfa/disable/${userId}`), {});
  }

  open(mfaToken: string, onSuccess: (response: any) => void, onCancel?: () => void): void {
    this.stateSubject.next({
      isOpen: true,
      context: { mfaToken, error: null, submitting: false, onSuccess, onCancel }
    });
  }

  submitOtp(code: string): void {
    const current = this.stateSubject.getValue();
    if (!current.context) {
      return;
    }

    this.stateSubject.next({ ...current, context: { ...current.context, submitting: true, error: null } });

    this.authService.verifyMfaToken(current.context.mfaToken, code).subscribe({
      next: (response: any) => {
        if (!response?.status || response.status.code === 200) {
          const onSuccess = this.stateSubject.getValue().context?.onSuccess;
          this.stateSubject.next({ isOpen: false, context: null });
          onSuccess?.(response);
        } else {
          this.setError(response.status.error || 'Invalid code. Please try again.');
        }
      },
      error: (error: any) => {
        const errorMessage = error?.error?.error?.message || 'Invalid code. Please try again.';
        this.setError(errorMessage);
      }
    });
  }

  private setError(error: string): void {
    const current = this.stateSubject.getValue();
    if (!current.context) {
      return;
    }
    this.stateSubject.next({ ...current, context: { ...current.context, submitting: false, error } });
  }

  close(): void {
    const onCancel = this.stateSubject.getValue().context?.onCancel;
    this.stateSubject.next({ isOpen: false, context: null });
    onCancel?.();
  }
}
